import { TFunction } from 'i18next';
import {
  FilterConfiguration,
  prioritizeEntryField,
} from 'stores/common/Filters';
import flatMap from 'lodash/flatMap';
import {
  compareByLevelAsc,
  TAXONOMIES_PRIORITIES,
} from 'stores/Catalog/config';
import { CatalogEntry, VocabularyNamespaces } from 'types';
import {
  PremiumSession,
  PremiumSessionContent,
  PremiumSessionAggregate,
} from 'types/premiumSession';

export type RangeFilter<T> = {
  from: T | undefined;
  to: T | undefined;
};

export type PremiumSessionContentWithSchedule = PremiumSessionContent & {
  scheduledSessions?: PremiumSession[];
};

/**
 * Returns the filter configurations for the premium catalog.
 * Notice that although this is premium session related, some filters are based on the CatalogEntry entity
 * this is why we have the PremiumSessionAggregate type.
 * @returns The filter configurations.
 */
export const getFilterConfigurations = (
  t: TFunction = (str: string) => str,
): FilterConfiguration<PremiumSessionAggregate>[] => {
  const filterConfigurations: FilterConfiguration<PremiumSessionAggregate>[] = [
    {
      key: 'taxonomyProducts',
      title: t('Products'),
      extractFilterValues: (entries) =>
        entries
          .map(({ catalogEntry }) =>
            prioritizeEntryField(
              catalogEntry,
              TAXONOMIES_PRIORITIES.products,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyProducts,
      validateEntry: ({ catalogEntry }, checkedTaxonomyProducts) => {
        const entryProducts: string[] = prioritizeEntryField(
          catalogEntry,
          TAXONOMIES_PRIORITIES.products,
          true,
        );
        const filtered =
          entryProducts?.filter((item) =>
            checkedTaxonomyProducts.includes(item),
          ) || [];
        return entryProducts?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'taxonomyTopics',
      title: t('Topics'),
      extractFilterValues: (entries) =>
        entries
          .map(({ catalogEntry }) =>
            prioritizeEntryField(
              catalogEntry,
              TAXONOMIES_PRIORITIES.topics,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyTopics,
      validateEntry: ({ catalogEntry }, checkedTopics) => {
        const entryTopics: string[] = prioritizeEntryField(
          catalogEntry,
          TAXONOMIES_PRIORITIES.topics,
          true,
        );
        const filtered = entryTopics?.filter((item) =>
          checkedTopics.includes(item),
        );

        return entryTopics?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'taxonomyAudiences',
      title: t('Audiences'),
      extractFilterValues: (entries) =>
        entries
          .map(({ catalogEntry }) =>
            prioritizeEntryField<CatalogEntry>(
              catalogEntry,
              TAXONOMIES_PRIORITIES.audiences,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyAudiences,
      validateEntry: ({ catalogEntry }, checkedAudiences) => {
        const entryAudiences: string[] = prioritizeEntryField<CatalogEntry>(
          catalogEntry,
          TAXONOMIES_PRIORITIES.audiences,
          true,
        );

        const filtered = entryAudiences?.filter((item) =>
          checkedAudiences.includes(item),
        );

        return entryAudiences?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'level',
      title: t('Levels'),
      groupIdenticalLabels: true,
      extractFilterValues: (entries) =>
        entries
          .map(({ catalogEntry }) =>
            prioritizeEntryField<CatalogEntry>(
              catalogEntry,
              TAXONOMIES_PRIORITIES.level,
              false,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingLevel,
      compareFn: compareByLevelAsc,
      validateEntry: ({ catalogEntry }, checkedLevels) => {
        const entryLevel = prioritizeEntryField<CatalogEntry>(
          catalogEntry,
          TAXONOMIES_PRIORITIES.level,
          false,
        );

        return checkedLevels.includes(`${entryLevel}`);
      },
    },
  ];

  filterConfigurations.push(
    {
      key: 'instructors',
      title: t('Instructors'),
      extractFilterValues: (entries) =>
        // Extracts the instructor usernames from all the scheduled sessions.
        flatMap(
          entries,
          (extendedEntry: PremiumSessionAggregate) =>
            extendedEntry.sessions?.flatMap((session) =>
              session.instructors.map((i) => ({
                token: i.username,
                display_name: i.name,
              })),
            ) || [],
        ),
      validateEntry: ({ sessions }, checkedInstructors) =>
        // Checks if any of the scheduled sessions has any of the selected instructors.
        sessions?.some((session) =>
          session.instructors?.some((i) =>
            checkedInstructors.includes(i.username),
          ),
        ),
    } as FilterConfiguration<PremiumSessionAggregate>,
    {
      key: 'languages',
      title: t('Languages'),
      vocabularyNamespace: VocabularyNamespaces.InstructionLanguages,
      extractFilterValues: (entries) =>
        flatMap(entries, (extendedEntry: PremiumSessionAggregate) =>
          extendedEntry.sessions?.flatMap(
            (session) => session.instruction_language,
          ),
        ),
      validateEntry: ({ sessions }, checkedLanguages) =>
        sessions?.some((session) =>
          checkedLanguages.includes(session.instruction_language),
        ),
    } as FilterConfiguration<PremiumSessionAggregate>,
  );

  return filterConfigurations;
};
